import { apiClient } from '../client';
import type { Product } from '../../components/Product/types';
import { stockRowsFromRules } from './availabilityRules';
import type {
  ApiProduct,
  ApiStock,
  ProductAvailabilityParams,
  ProductAvailabilityResponse,
  ProductsListResponse,
  ProductsListParams,
  ProductShowResponse,
} from './types';

// Query keys
export const productsQueryKeys = {
  all: ['products'] as const,
  lists: () => [...productsQueryKeys.all, 'list'] as const,
  list: (params?: ProductsListParams) => [...productsQueryKeys.lists(), params] as const,
  details: () => [...productsQueryKeys.all, 'detail'] as const,
  detail: (id: number) => [...productsQueryKeys.details(), id] as const,
  availabilities: () => [...productsQueryKeys.all, 'availability'] as const,
  availability: (params: ProductAvailabilityParams) => [...productsQueryKeys.availabilities(), params] as const,
};

// API call
export async function getProducts(params?: ProductsListParams): Promise<ProductsListResponse> {
  const response = await apiClient.get<ProductsListResponse>('/api/products', { params });
  return response.data;
}

export async function getProductAvailability(
  params: ProductAvailabilityParams,
): Promise<ProductAvailabilityResponse> {
  const response = await apiClient.get<ProductAvailabilityResponse>('/api/products/availability', { params });
  return response.data;
}

// Map API response to frontend Product type
function formatServingSize(amount: number | null, unit: string | null): string | null {
  if (amount == null) return null;
  return unit ? `${amount} ${unit}` : String(amount);
}

function formatStocksRange(stocks: ApiStock[]): { start: string | null; end: string | null } {
  if (stocks.length === 0) return { start: null, end: null };
  const withStart = stocks.filter((s) => s.start_date != null);
  const withEnd = stocks.filter((s) => s.end_date != null);
  const start = withStart.length > 0
    ? withStart.reduce((min, s) => s.start_date! < min ? s.start_date! : min, withStart[0].start_date!)
    : null;
  const end = withEnd.length > 0
    ? withEnd.reduce((max, s) => s.end_date! > max ? s.end_date! : max, withEnd[0].end_date!)
    : null;
  return { start, end };
}

// Shared base mapping from ApiProduct to Product
function mapBaseProduct(api: ApiProduct): Omit<Product, 'images' | 'inventory_items' | 'container_items' | 'stock_items'> {
  const stocks = formatStocksRange(stockRowsFromRules(api.availability_rules));

  return {
    id: api.id,
    name: api.name,
    category: api.category,
    form: api.form as Product['form'],
    kitchen: api.kitchen ?? null,
    kitchen_section: api.kitchen_section ?? [],
    papercut_category: api.papercut_category ?? null,
    serving_temperature: api.serving_temperature,
    cost_to_company: api.food_cost,
    recommended_retail_price: null,
    price_excluding_gst: api.price_excluding_gst,
    price_including_gst: api.price_including_gst,
    archived_at: null,
    catalogues: api.catalogues.map((c) => c.name),
    active_period_start: api.serving_period?.start_date ?? null,
    active_period_end: api.serving_period?.end_date ?? null,
    unavailability: api.unavailability ?? null,
    food_cost: api.food_cost,
    food_cost_percent: api.food_cost_percent,
    allergen_tags: api.allergen_tags,
    nutrition_tags: api.nutrition_tags,
    chef_recommended: api.chef_recommended,
    bcrs: api.bcrs,
    display_unit: api.display_unit,
    ingredients: api.ingredients,
    serving_size: formatServingSize(api.portion_amount, api.portion_unit_label),
    key_serving_info: api.notes,
    chinese_name: api.chinese_name,
    production_notes: api.production_notes,
    fulfilment_notes: api.fulfilment_notes,
    byline: api.byline,
    story: api.description,
    stocks_start: stocks.start,
    stocks_end: stocks.end,
    status: api.status as Product['status'],
    active_status: api.active_status,
    macros: api.macros,
    diet_tags: api.diet_tags,
    created_at: api.created_at,
  };
}

// Single product fetch
export async function getProduct(id: number): Promise<ProductShowResponse> {
  const response = await apiClient.get<ProductShowResponse>(`/api/products/${id}`);
  return response.data;
}

// Map for detail views (includes equipment, images, stocks)
export function mapApiProductToProduct(api: ApiProduct): Product {
  return {
    ...mapBaseProduct(api),
    images: api.images.map((img) => ({
      signed_id: img.signed_id,
      url: img.url,
      filename: img.filename,
    })),
    display_attachment: api.display_attachment
      ? {
          signed_id: api.display_attachment.signed_id,
          url: api.display_attachment.url,
          filename: api.display_attachment.filename,
        }
      : null,
    inventory_items: api.product_inventories.map((pi) => ({
      id: pi.inventory.id,
      name: pi.inventory.name,
      category: pi.inventory.category,
      quantity: pi.quantity,
      image_url: pi.inventory.image_url,
    })),
    container_items: api.product_containers.map((pc) => ({
      id: pc.inventory.id,
      name: pc.inventory.name,
      category: pc.inventory.category,
      capacity: pc.capacity,
      minimum_pax_required: pc.minimum_pax_required,
      image_url: pc.inventory.image_url,
    })),
    stock_items: stockRowsFromRules(api.availability_rules).map((s) => ({
      id: s.id,
      quantity: s.quantity,
      start_date: s.start_date,
      end_date: s.end_date,
    })),
  };
}
